import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";

const TABS = [
  { id: "business", label: "Business quiz" },
  { id: "project", label: "Project quiz" },
  { id: "self_assessment", label: "Self-assessment leads" },
  { id: "completions", label: "Anonymous completions" },
];
const STAGE_LABEL = { 1: "1 · Profile", 2: "2 · Documents", 3: "3 · Analyse docs", 4: "4 · Score", 5: "5 · Report" };
const REV = { lt_100k: "<100K/mo", "100k_1m": "100K–1M/mo", "1m_5m": "1M–5M/mo", "5m_15m": "5M–15M/mo", gt_15m: ">15M/mo" };
const RAISE = { under_1m: "<1M", "1m_5m": "1–5M", "5m_15m": "5–15M", "15m_25m": "15–25M", "25m_50m": "25–50M", "50m_100m": "50–100M", "100m_500m": "100–500M", over_500m: ">500M" };
const PSTAGE = { s1: "Concept", s2: "Feasibility", s3: "Development", s4: "Fin. close", s5: "Construction", s6: "Operational" };

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const scoreCls = (n) => n == null ? "text-neutral-500" : n >= 71 ? "text-emerald-300" : n >= 51 ? "text-lime-300" : n >= 31 ? "text-amber-300" : "text-orange-300";

const ScorePill = ({ n }) => (
  <span className={`rounded-md bg-neutral-900 px-2 py-0.5 text-sm font-extrabold tabular-nums ${scoreCls(n)}`}>{n ?? "—"}</span>
);

function Answers({ kind, a }) {
  if (!a) return null;
  const chips = kind === "project"
    ? [a.category?.replace(/_/g, " "), PSTAGE[a.stage], a.land?.replace(/_/g, " "), a.offtake && `offtake ${a.offtake}`, a.equity && `equity ${a.equity}`, a.raise && `raise ${RAISE[a.raise] || a.raise}`]
    : [a.stage?.replace(/_/g, " "), REV[a.revenue_band], a.is_profitable && `profit: ${a.is_profitable}`, ...(a.purposes || []).map((p) => p.replace(/_/g, " ")), a.raise && `raise ${RAISE[a.raise] || a.raise}`];
  return (
    <div className="flex flex-wrap gap-1">
      {chips.filter(Boolean).map((c, i) => (
        <span key={i} className="rounded-full border border-neutral-700 bg-neutral-900 px-2 py-0.5 text-[10px] text-neutral-300">{c}</span>
      ))}
    </div>
  );
}

function FounderRows({ kind, rows }) {
  return rows.map((r) => (
    <tr key={r.company_id} data-testid={`quiz-lead-row-${r.company_id}`} className="align-top">
      <td className="px-3 py-3">
        <p className="font-semibold text-neutral-100">{r.founder_name || "—"}</p>
        <p className="text-[11px] text-neutral-500">{r.email}</p>
        <p className="text-[11px] text-neutral-500">{r.company_name}</p>
      </td>
      <td className="px-3 py-3 text-neutral-400">{fmtDate(r.quiz_completed_at || r.signed_up_at)}</td>
      <td className="px-3 py-3"><ScorePill n={r.preliminary_score} /></td>
      <td className="px-3 py-3 min-w-[260px]"><Answers kind={kind} a={r.answers} /></td>
      <td className="px-3 py-3 text-neutral-300">{STAGE_LABEL[r.stage] || r.stage}</td>
      <td className="px-3 py-3"><ScorePill n={r.current_score || null} /></td>
      <td className="px-3 py-3 text-neutral-300">{r.docs}</td>
      <td className="px-3 py-3">{r.report_downloaded ? <span className="text-emerald-300">Yes</span> : <span className="text-neutral-500">No</span>}</td>
      <td className="px-3 py-3 text-neutral-300">{r.rm_name || <span className="text-neutral-600">Unassigned</span>}</td>
      <td className="px-3 py-3 text-neutral-400">{fmtDate(r.last_login_at)}</td>
    </tr>
  ));
}

export default function AdminQuizLeads() {
  const [tab, setTab] = useState("business");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/quiz-leads", { params: { kind: tab } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ rows: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
  }, [data, q]);
  const avgPrelim = useMemo(() => {
    const v = rows.map((r) => r.preliminary_score ?? r.score).filter((n) => typeof n === "number");
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
  }, [rows]);

  const isFounder = tab === "business" || tab === "project";
  return (
    <AdminShell current="/admin/quiz-leads">
      <div className="px-6 py-6">
        <h1 className="text-xl font-bold text-white" data-testid="quiz-leads-title">Quiz Leads</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Founders who arrived through the Capital / Project Readiness quiz, with their preliminary score and where they are now — for follow-up.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`quiz-leads-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold ${tab === t.id
                ? "bg-[#0A8B85]/20 text-emerald-300 ring-1 ring-[#0A8B85]/50" : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}>
              {t.label}
            </button>
          ))}
          <input data-testid="quiz-leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…"
            className="ml-auto w-64 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-sm text-neutral-100 outline-none focus:border-[#0A8B85]" />
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-xs text-neutral-400" data-testid="quiz-leads-stats">
          <span className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5">Shown: <b className="text-neutral-100">{rows.length}</b></span>
          {avgPrelim != null && <span className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5">Avg preliminary score: <b className={scoreCls(avgPrelim)}>{avgPrelim}</b></span>}
          {tab === "completions" && data && (
            <>
              <span className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5">Completed: <b className="text-neutral-100">{data.total}</b></span>
              <span className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5">Signed up: <b className="text-emerald-300">{data.converted}</b> ({data.total ? Math.round(100 * data.converted / data.total) : 0}%)</span>
              <span className="rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5">Shared: <b className="text-sky-300">{data.shared}</b></span>
            </>
          )}
        </div>

        <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-800">
          <table className="w-full min-w-[1100px] text-left text-sm" data-testid="quiz-leads-table">
            <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
              {isFounder ? (
                <tr>
                  <th className="px-3 py-3">Founder</th><th className="px-3 py-3">Quiz date</th><th className="px-3 py-3">Prelim.</th>
                  <th className="px-3 py-3">Quiz answers</th><th className="px-3 py-3">Stage now</th><th className="px-3 py-3">{tab === "project" ? "PRS" : "CRS"}</th>
                  <th className="px-3 py-3">Docs</th><th className="px-3 py-3">Report</th><th className="px-3 py-3">RM</th><th className="px-3 py-3">Last login</th>
                </tr>
              ) : tab === "self_assessment" ? (
                <tr><th className="px-3 py-3">Email</th><th className="px-3 py-3">Date</th><th className="px-3 py-3">Score</th><th className="px-3 py-3">Band</th></tr>
              ) : (
                <tr><th className="px-3 py-3">Date</th><th className="px-3 py-3">Quiz</th><th className="px-3 py-3">Score</th><th className="px-3 py-3">Band</th><th className="px-3 py-3">Answers</th><th className="px-3 py-3">Shared</th><th className="px-3 py-3">Signed up</th></tr>
              )}
            </thead>
            <tbody className="divide-y divide-neutral-800 bg-neutral-950">
              {loading ? (
                <tr><td colSpan="10" className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-10 text-center text-neutral-500" data-testid="quiz-leads-empty">No leads yet.</td></tr>
              ) : isFounder ? (
                <FounderRows kind={tab} rows={rows} />
              ) : tab === "self_assessment" ? rows.map((r) => (
                <tr key={r.lead_id} data-testid={`quiz-lead-row-${r.lead_id}`}>
                  <td className="px-3 py-3 text-neutral-100">{r.email}</td>
                  <td className="px-3 py-3 text-neutral-400">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-3"><ScorePill n={r.score} /></td>
                  <td className="px-3 py-3 text-neutral-300">{r.band}</td>
                </tr>
              )) : rows.map((r) => (
                <tr key={r.completion_id} data-testid={`quiz-lead-row-${r.completion_id}`} className="align-top">
                  <td className="px-3 py-3 text-neutral-400">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-3 capitalize text-neutral-300">{r.quiz}</td>
                  <td className="px-3 py-3"><ScorePill n={r.score} /></td>
                  <td className="px-3 py-3 text-neutral-300">{r.band}</td>
                  <td className="px-3 py-3 min-w-[260px]"><Answers kind={r.quiz} a={r.quiz === "project" ? r.answers : { stage: r.answers?.stage, revenue_band: r.answers?.revenueBand, is_profitable: r.answers?.isProfitable, purposes: r.answers?.purposes, raise: r.answers?.raiseBracket }} /></td>
                  <td className="px-3 py-3 text-neutral-300">{(r.shares || []).map((s) => s.channel).join(", ") || "—"}</td>
                  <td className="px-3 py-3">{r.converted_user_id ? <span className="text-emerald-300">Yes</span> : <span className="text-neutral-500">No</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}
